import * as Sentry from "@sentry/browser";

import type { RealtimeMessageData } from "./realtimeTypes";

export interface MessageSequencerProps {
  onMessageProcessed: (data: RealtimeMessageData) => void;
  isDevOrStaging?: boolean;
}

export interface SequencerStreamDiagnostics {
  currentSequence: number;
  expectedSequence: number;
  pendingCount: number;
  pendingSequences: number[];
}

export interface SequencerDiagnostics {
  streams: Record<string, SequencerStreamDiagnostics>;
  dedupIdCount: number;
}

/**
 * Maximum number of buffered messages before emitting a streaming error.
 *
 * This value is set high because Redis pub/sub can deliver messages
 * severely out of order, requiring a large buffer to ensure correct sequencing.
 */
const maxBufferedMessages = 2000;

/**
 * Maximum message IDs tracked per dedup generation. When exceeded, the current
 * generation is rotated to "previous" and a fresh generation starts. Two generations
 * are kept so that recent duplicates are still caught during the transition.
 */
const maxDedupIdsPerGeneration = 5_000;

/**
 * MessageSequencer handles out-of-order message delivery using sequence numbers.
 *
 * The sequencer is transport/routing agnostic — it accepts and emits RealtimeMessageData
 * without knowing about channels. The caller is responsible for routing the processed
 * messages to the appropriate destination.
 *
 * Key behaviors:
 * - Buffers out-of-order messages and drains them when gaps are filled
 * - Detects new streams when sequence resets to 1
 * - Emits a synthetic error if the buffer exceeds maxBufferedMessages
 * - Deduplicates messages by message_id using a bounded two-generation cache
 */
export class MessageSequencer {
  private messageSequences: Map<string, number> = new Map();
  private pendingMessages: Map<string, Map<number, RealtimeMessageData>> = new Map();

  private currentDedupIds: Set<string> = new Set();
  private previousDedupIds: Set<string> = new Set();

  // Dev/staging only: records wire-order arrival of sequences per stream for console debugging
  private receivedSequenceLog: Map<string, number[]> | null = null;

  private onMessageProcessed: (data: RealtimeMessageData) => void;
  private isDevOrStaging: boolean;

  constructor({ onMessageProcessed, isDevOrStaging = false }: MessageSequencerProps) {
    this.onMessageProcessed = onMessageProcessed;
    this.isDevOrStaging = isDevOrStaging;
    if (isDevOrStaging) {
      this.receivedSequenceLog = new Map();
    }
  }

  private log(message: string, extra?: Record<string, unknown>): void {
    if (this.isDevOrStaging) {
      console.log(`[DUST Sequencer] ${message}`, extra ?? "");
    }
  }

  private hasSeenMessageId(id: string): boolean {
    return this.currentDedupIds.has(id) || this.previousDedupIds.has(id);
  }

  private trackMessageId(id: string): void {
    this.currentDedupIds.add(id);
    if (this.currentDedupIds.size > maxDedupIdsPerGeneration) {
      this.previousDedupIds = this.currentDedupIds;
      this.currentDedupIds = new Set();
    }
  }

  getDiagnostics(): SequencerDiagnostics {
    const streams: Record<string, SequencerStreamDiagnostics> = {};
    for (const [streamId, currentSequence] of this.messageSequences) {
      const pending = this.pendingMessages.get(streamId);
      streams[streamId] = {
        currentSequence,
        expectedSequence: currentSequence + 1,
        pendingCount: pending?.size ?? 0,
        pendingSequences: pending
          ? Array.from(pending.keys())
              .sort((a, b) => a - b)
              .slice(0, 50)
          : [],
      };
    }
    return {
      streams,
      dedupIdCount: this.currentDedupIds.size + this.previousDedupIds.size,
    };
  }

  /**
   * Returns the wire-order log of received messaging sequences per stream_id (dev/staging only).
   */
  getReceivedSequenceLog(): Record<string, number[]> | null {
    if (!this.receivedSequenceLog) {
      return null;
    }
    const result: Record<string, number[]> = {};
    for (const [streamId, sequences] of this.receivedSequenceLog) {
      result[streamId] = sequences;
    }
    return result;
  }

  /**
   * Clears all state for a completed stream to prevent memory leaks.
   */
  cleanupStream(streamId: string): void {
    this.messageSequences.delete(streamId);
    this.pendingMessages.delete(streamId);
    this.receivedSequenceLog?.delete(streamId);
    this.log(`Stream cleaned up`, { streamId });
  }

  /**
   * Process a message with sequencing and deduplication.
   * Messages without sequence/stream_id are passed through immediately.
   */
  processMessage(data: RealtimeMessageData): void {
    if (data.message_id) {
      if (this.hasSeenMessageId(data.message_id)) {
        this.log(`Dedup drop`, { message_id: data.message_id, sequence: data.sequence, stream_id: data.stream_id });
        return;
      }
      this.trackMessageId(data.message_id);
    }

    if (data.sequence === undefined || !data.stream_id) {
      this.onMessageProcessed(data);
      return;
    }

    const streamId = data.stream_id;
    const sequence = data.sequence;
    const currentSequence = this.messageSequences.get(streamId) || 0;
    const expectedSequence = currentSequence + 1;

    if (this.receivedSequenceLog) {
      const existing = this.receivedSequenceLog.get(streamId);
      if (existing) {
        existing.push(sequence);
      } else {
        this.receivedSequenceLog.set(streamId, [sequence]);
      }
    }

    if (sequence === expectedSequence) {
      this.messageSequences.set(streamId, sequence);
      this.onMessageProcessed(data);
      this.drainBuffer(streamId);
    } else if (sequence > expectedSequence) {
      const isNewGap = !this.pendingMessages.has(streamId);
      if (isNewGap) {
        this.pendingMessages.set(streamId, new Map());
      }
      const buffer = this.pendingMessages.get(streamId)!;
      buffer.set(sequence, data);

      this.log(`Buffered out-of-order message`, {
        sequence,
        expectedSequence,
        gap: sequence - expectedSequence,
        bufferSize: buffer.size,
        streamId,
      });

      if (isNewGap) {
        Sentry.addBreadcrumb({
          category: "dust-sequencer",
          message: `Gap detected: received seq=${sequence}, waiting for seq=${expectedSequence}`,
          level: "warning",
          data: { streamId, sequence, expectedSequence, gap: sequence - expectedSequence },
        });
      } else {
        Sentry.addBreadcrumb({
          category: "dust-sequencer",
          message: `Buffered seq=${sequence}, expected=${expectedSequence}, buf=${buffer.size}`,
          level: "info",
        });
      }

      if (buffer.size >= maxBufferedMessages) {
        this.emitBufferOverflowError(data, streamId, expectedSequence);
      }
    } else if (sequence === 1 && currentSequence > 1) {
      this.log(`New conversation detected`, { streamId, previousSequence: currentSequence });
      this.pendingMessages.delete(streamId);
      this.messageSequences.set(streamId, sequence);
      if (this.receivedSequenceLog) {
        this.receivedSequenceLog.set(streamId, [sequence]);
      }
      this.onMessageProcessed(data);
    } else {
      this.log(`Old/duplicate seq drop`, { sequence, expectedSequence, streamId });
    }
  }

  private emitBufferOverflowError(data: RealtimeMessageData, streamId: string, expectedSequence: number): void {
    const buffer = this.pendingMessages.get(streamId);
    const pendingSample = buffer
      ? Array.from(buffer.keys())
          .sort((a, b) => a - b)
          .slice(0, 20)
      : [];

    this.log(`Buffer overflow`, { streamId, expectedSequence, pendingSample });

    this.pendingMessages.delete(streamId);
    this.messageSequences.delete(streamId);

    this.onMessageProcessed({
      event: data.event,
      stream_id: streamId,
      payload: {
        type: "error",
        error_type: "dust-message-sequence-error",
        request_id: (data.payload as { request_id?: string })?.request_id,
        stream_id: streamId,
        message: `Message buffer exceeded ${maxBufferedMessages} messages. Redis pub/sub is severely reordering messages.`,
        expectedSequence,
      },
    });
  }

  /**
   * Drain all consecutive buffered messages starting from the current expected sequence.
   */
  private drainBuffer(streamId: string): void {
    const pending = this.pendingMessages.get(streamId);
    if (!pending || pending.size === 0) {
      return;
    }

    let currentSequence = this.messageSequences.get(streamId) || 0;
    let nextMessage = pending.get(currentSequence + 1);
    let drainCount = 0;

    while (nextMessage) {
      currentSequence += 1;
      this.messageSequences.set(streamId, currentSequence);
      pending.delete(currentSequence);
      this.onMessageProcessed(nextMessage);
      drainCount++;

      nextMessage = pending.get(currentSequence + 1);
    }

    if (drainCount > 0) {
      this.log(`Drained ${drainCount} buffered messages`, {
        streamId,
        currentSequence,
        remainingBuffered: pending.size,
      });
    }

    if (pending.size === 0) {
      this.pendingMessages.delete(streamId);
    }
  }
}
